import { Input, Stack, Text, Textarea } from "@chakra-ui/react";
import * as React from "react";
import { Control, Controller, FieldErrors } from "react-hook-form";
import { z } from "zod";

import {
  CampaignCriteria,
  CampaignCriteriaRequest,
} from "../api/campaign-criteria";
import { brand } from "../lib/brand";
import { Field } from "./field";
import { InfoTip } from "./info-tip";

export const campaignCriteriaSchema = z.object({
  offer: z.string().trim().min(1, "Tell us what this ad group is offering"),
  targetCustomer: z.string(),
  customerAction: z.string().trim().min(1, "Tell us what you want them to do"),
  icpButBadFit: z.string(),
});

export type CampaignCriteriaFormData = z.infer<typeof campaignCriteriaSchema>;

export const EMPTY_CRITERIA_FORM: CampaignCriteriaFormData = {
  offer: "",
  targetCustomer: "",
  customerAction: "",
  icpButBadFit: "",
};

export const criteriaToFormValues = (
  criteria: CampaignCriteria,
): CampaignCriteriaFormData => ({
  offer: criteria.offer,
  targetCustomer: criteria["target-customer"] ?? "",
  customerAction: criteria["customer-action"],
  icpButBadFit: criteria["icp-but-bad-fit"] ?? "",
});

// The optional fields go out as null rather than "" so the backend clears
// them rather than storing a blank.
export const formValuesToRequest = (
  data: CampaignCriteriaFormData,
): CampaignCriteriaRequest => ({
  offer: data.offer.trim(),
  "target-customer": data.targetCustomer.trim() || null,
  "customer-action": data.customerAction.trim(),
  "icp-but-bad-fit": data.icpButBadFit.trim() || null,
});

const OptionalLabel = ({ children }: { children: React.ReactNode }) => (
  <>
    {children}
    <Text as="span" color="fg.subtle" fontWeight="normal">
      {" "}
      — Optional
    </Text>
  </>
);

// The guidance rides on the label as a hover tip rather than sitting
// under the field, so the form reads as four questions rather than
// eight paragraphs. Same affordance as the metric cards.
const LabelTip = ({ children }: { children: React.ReactNode }) => (
  <Text as="span" display="inline-flex" marginLeft="1.5">
    <InfoTip content={children} />
  </Text>
);

interface CampaignCriteriaFieldsProps {
  control: Control<CampaignCriteriaFormData>;
  errors: FieldErrors<CampaignCriteriaFormData>;
}

// Shared by the turn-on wizard and the edit dialog so the wording can't
// drift.
export function CampaignCriteriaFields({
  control,
  errors,
}: CampaignCriteriaFieldsProps) {
  return (
    <Stack gap="4">
      <Field
        label={
          <>
            What is this ad group offering?
            <LabelTip>
              This is how {brand.productName} knows what you&rsquo;re actually
              selling &mdash; whether a search is for your product, or for
              something that just sounds like it.
            </LabelTip>
          </>
        }
        required
        invalid={!!errors.offer}
        errorText={errors.offer?.message}
      >
        <Controller
          name="offer"
          control={control}
          render={({ field }) => (
            <Textarea
              autoFocus
              rows={2}
              size="md"
              placeholder="e.g. project management software for construction teams"
              {...field}
            />
          )}
        />
      </Field>

      <Field
        label={
          <>
            <OptionalLabel>Who is it for?</OptionalLabel>
            <LabelTip>
              Leave blank if it&rsquo;s your whole ICP. This only narrows it.
            </LabelTip>
          </>
        }
        invalid={!!errors.targetCustomer}
        errorText={errors.targetCustomer?.message}
      >
        <Controller
          name="targetCustomer"
          control={control}
          render={({ field }) => (
            <Textarea
              rows={2}
              size="md"
              placeholder="e.g. project managers at mid-size construction firms"
              {...field}
            />
          )}
        />
      </Field>

      <Field
        label={
          <>
            What do you want them to do?
            <LabelTip>
              Ex. If your goal is to generate downloads of a white paper, you
              might tolerate searches from people just researching. If your goal
              is to book demos, you might not.
            </LabelTip>
          </>
        }
        required
        invalid={!!errors.customerAction}
        errorText={errors.customerAction?.message}
      >
        <Controller
          name="customerAction"
          control={control}
          render={({ field }) => (
            <Input size="md" placeholder="e.g. book a demo" {...field} />
          )}
        />
      </Field>

      <Field
        label={
          <>
            <OptionalLabel>Who looks like a fit but isn&rsquo;t?</OptionalLabel>
            <LabelTip>
              Searchers who might look like your ICP on paper but aren&rsquo;t
              really a good fit or qualified.
            </LabelTip>
          </>
        }
        invalid={!!errors.icpButBadFit}
        errorText={errors.icpButBadFit?.message}
      >
        <Controller
          name="icpButBadFit"
          control={control}
          render={({ field }) => (
            <Textarea
              rows={2}
              size="md"
              placeholder="e.g. construction software searches are a fit unless they mention accounting, which we don't offer"
              {...field}
            />
          )}
        />
      </Field>
    </Stack>
  );
}
